import type { CSSProperties } from 'react';
import { classifyRI, RiStatus } from '@leopart/calc';
import { getEffortBin } from './useTeamEffort';
import { ICombinedAthleteRow } from './effortRecoveryJoin';
import { roleLabel } from '../../utils/roleLabel';
import { useKeyboardActivate } from '../../../hooks/useKeyboardActivate';
import effortStyles from './TeamEffortTab.module.scss';
import styles from './TeamEffortRecoveryView.module.scss';

interface EffortRecoveryRowProps {
  row: ICombinedAthleteRow;
  onSelect?: (athleteId: string, athleteName?: string) => void;
}

/** Colore metà "Sforzo" — stessi 5 bin/token della heatmap sRPE */
const EFFORT_BIN_VAR = [
  'var(--effort-bin0-bg)',
  'var(--effort-bin1-bg)',
  'var(--effort-bin2-bg)',
  'var(--effort-bin3-bg)',
  'var(--effort-bin4-bg)',
];

/** Colore metà "Recovery" — stesse 4 fasce RI di TeamRecoveryView */
const RI_VAR: Record<RiStatus, string> = {
  good: 'var(--ri-good)',
  normal: 'var(--ri-normal)',
  warning: 'var(--ri-warning)',
  critical: 'var(--ri-critical)',
};

/** Testo (foreground) contrasto-aware per il numero sRPE — stessi 5 bin */
const EFFORT_BIN_FG_VAR = [
  'var(--effort-bin0-fg)',
  'var(--effort-bin1-fg)',
  'var(--effort-bin2-fg)',
  'var(--effort-bin3-fg)',
  'var(--effort-bin4-fg)',
];

/** Testo (foreground) contrasto-aware per il numero RI — token theme-invariant */
const RI_FG_VAR: Record<RiStatus, string> = {
  good: 'var(--ri-good-fg)',
  normal: 'var(--ri-normal-fg)',
  warning: 'var(--ri-warning-fg)',
  critical: 'var(--ri-critical-fg)',
};

/** Metà neutra quando solo uno dei due valori è disponibile (non un bin reale) */
const NEUTRAL_VAR = 'var(--c-chip-bg)';

/** Tooltip cella: solo i valori disponibili, "Dato mancante" se nessuno dei due */
function cellTitle(srpe: number | null, ri: number | null): string {
  const parts: string[] = [];
  if (srpe != null) parts.push(`sRPE ${Math.round(srpe)}`);
  if (ri != null) parts.push(`RI ${ri}`);
  return parts.length > 0 ? parts.join(' · ') : 'Dato mancante';
}

/**
 * Riga heatmap "Sforzo + Recovery": atleta + 7 celle bicolori (diagonale
 * 135°, alto-sx = sRPE, basso-dx = RI). Il numero sRPE (arrotondato) e il
 * numero RI (grezzo, come nella vista Recovery pura) sono resi come nodi di
 * testo condizionali sopra il colore, oltre a colore + tooltip con i valori
 * disponibili.
 */
export function EffortRecoveryRow({ row, onSelect }: EffortRecoveryRowProps) {
  const fullName = [row.athlete.firstName, row.athlete.lastName].filter(Boolean).join(' ') || '—';
  const activateProps = useKeyboardActivate(onSelect ? () => onSelect(row.athlete.id, fullName) : undefined);

  return (
    <tr className={effortStyles.row} onClick={() => onSelect?.(row.athlete.id, fullName)} {...activateProps}>
      <td className={effortStyles.tdAthlete}>
        <span className={effortStyles.athleteName}>{fullName}</span>
        {row.athlete.role && (
          <span className={effortStyles.athleteRole}>{roleLabel(row.athlete.role)}</span>
        )}
      </td>

      {row.cells.map((cell) => {
        const isEmpty = cell.srpe == null && cell.ri == null;
        const effColor =
          cell.srpe != null ? EFFORT_BIN_VAR[getEffortBin('srpe', cell.srpe)] : NEUTRAL_VAR;
        const recoColor = cell.ri != null ? RI_VAR[classifyRI(cell.ri)] : NEUTRAL_VAR;
        // Foreground guard-null: classifyRI(null) risolverebbe a 'critical', quindi
        // il calcolo va fatto SOLO quando il valore esiste davvero.
        const effFg =
          cell.srpe != null ? EFFORT_BIN_FG_VAR[getEffortBin('srpe', cell.srpe)] : undefined;
        const recoFg = cell.ri != null ? RI_FG_VAR[classifyRI(cell.ri)] : undefined;
        const label = cellTitle(cell.srpe, cell.ri);

        return (
          <td key={cell.date} className={effortStyles.tdDay} title={label}>
            <div
              className={`${styles.biCell} ${isEmpty ? effortStyles.missing : styles.biCellSplit}`}
              // eccezione inline ammessa: colori calcolati a runtime, letti dal CSS via var()
              style={
                isEmpty
                  ? undefined
                  : ({
                      '--c-eff': effColor,
                      '--c-reco': recoColor,
                      '--c-eff-fg': effFg,
                      '--c-reco-fg': recoFg,
                    } as CSSProperties)
              }
              // il valore resta accessibile anche via aria-label (screen reader) e
              // focus da tastiera, oltre al testo visibile nei due span sotto
              role="img"
              tabIndex={0}
              aria-label={label}
            >
              {cell.srpe != null && <span className={styles.numEff}>{Math.round(cell.srpe)}</span>}
              {cell.ri != null && <span className={styles.numReco}>{cell.ri}</span>}
            </div>
          </td>
        );
      })}
    </tr>
  );
}

export default EffortRecoveryRow;
